// Pure helpers for the Zendesk page: change chips, shares, relative times. No React, easy to test.

export const SENTIMENTS = ["negative", "neutral", "positive"];

/** {dir: "up" | "down" | "flat" | "new", pct} for a week-over-week change; null change means no last week. */
export function changeOf(changePct) {
  if (changePct === null || changePct === undefined) return { dir: "new", pct: null };
  if (changePct > 0) return { dir: "up", pct: changePct };
  if (changePct < 0) return { dir: "down", pct: Math.abs(changePct) };
  return { dir: "flat", pct: 0 };
}

/** Week-over-week change of the total, rounded to one decimal; null when last week was empty. */
export function totalChangePct(thisWeek, lastWeek) {
  if (!lastWeek) return null;
  return Math.round(((thisWeek - lastWeek) * 1000) / lastWeek) / 10;
}

/** Share of the week's tickets that came from the biggest cause, as a whole percent. */
export function topCauseShare(report) {
  const top = report?.top_causes?.[0];
  if (!top || !report.total_this_week) return null;
  return Math.round((top.this_week * 100) / report.total_this_week);
}

/** Sentiment counts → whole percents that add up to 100 (largest remainder). */
export function sentimentShares(counts) {
  const total = SENTIMENTS.reduce((sum, k) => sum + (counts?.[k] || 0), 0);
  if (!total) return null;
  const raw = SENTIMENTS.map((k) => ((counts?.[k] || 0) * 100) / total);
  const floors = raw.map(Math.floor);
  let left = 100 - floors.reduce((a, b) => a + b, 0);
  raw
    .map((v, i) => [v - floors[i], i])
    .sort((a, b) => b[0] - a[0])
    .forEach(([, i]) => {
      if (left > 0) {
        floors[i] += 1;
        left -= 1;
      }
    });
  return SENTIMENTS.reduce((out, k, i) => ({ ...out, [k]: floors[i] }), {});
}

/** Bar heights in percent of the largest value (at least 2% so empty days stay visible). */
export function barHeights(values) {
  const max = Math.max(1, ...(values || []));
  return (values || []).map((v) => Math.max(2, Math.round((v * 100) / max)));
}

const UNITS = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "2 hours ago" / "yesterday" in the user's language. */
export function timeAgo(iso, language = "en", now = Date.now()) {
  const ts = Date.parse(iso || "");
  if (Number.isNaN(ts)) return "";
  const seconds = Math.round((ts - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(0, "minute");
}

const CHANNEL_KEYS = {
  email: "email",
  web: "web",
  api: "api",
  chat: "chat",
  native_messaging: "messaging",
  messaging: "messaging",
  whatsapp: "messaging",
  voice: "voice",
};

/** Translation key suffix for a Zendesk via channel ("native_messaging" → "messaging"). */
export const channelKey = (channel) => CHANNEL_KEYS[channel] || "other";
